import type { ImageProps } from "next/image";
import type {
  SanityImageAsset,
  SanityImageCrop,
  SanityImageHotspot
} from "@/types/sanity.types";

/**
 * Sanity Image Asset Reference type (before dereferencing)
 */
type SanityImageAssetReference = {
  _ref: string;
  _type: "reference";
  _weak?: boolean;
};

/**
 * Partial Sanity Image Asset (from UI library or GraphQL)
 */
type PartialSanityImageAsset = {
  _id?: string;
  metadata?: {
    preview?: string;
  };
};

/**
 * Sanity Image type with asset, crop, and hotspot
 * This type is compatible with both:
 * - Dereferenced Sanity image objects (using asset-> in GROQ)
 * - Non-dereferenced image references
 * - Partial image assets from UI libraries
 */
export type SanityImageType = {
  asset?:
    | SanityImageAsset
    | SanityImageAssetReference
    | PartialSanityImageAsset;
  crop?: Partial<SanityImageCrop>;
  hotspot?: Partial<SanityImageHotspot>;
  alt?: string;
  _type?: string;
  __typename?: string;
};

/**
 * Type guard to check if an asset is a dereferenced SanityImageAsset
 */
export function isImageAsset(
  asset:
    | SanityImageAsset
    | SanityImageAssetReference
    | { _id?: string }
    | undefined
): asset is SanityImageAsset {
  if (!asset) return false;
  // Check if it has _id (dereferenced) and doesn't have _ref (reference)
  return "_id" in asset && typeof asset._id === "string" && !("_ref" in asset);
}

/**
 * Predefined aspect ratio variants for common use cases
 */
export type AspectRatioVariant =
  | "hero" // 21:9 ultra-wide hero
  | "wide" // 16:9 standard wide
  | "video" // 16:9 video format
  | "landscape" // 4:3 landscape
  | "square" // 1:1 square
  | "portrait" // 3:4 portrait
  | "card" // 3:2 card format
  | "auto"; // Use original aspect ratio

/**
 * Props for both server and client Sanity Image components
 */
export type SanityImageProps = {
  /** Sanity image object with asset, crop, and hotspot */
  image?: SanityImageType | null;
  /** Alt text for accessibility */
  alt?: string;
  /**
   * Fit mode: crop fills the space, original maintains aspect ratio
   * Default: "crop" when fill=true, "original" when using fixed dimensions
   */
  fit?: "crop" | "original";
  /** Image quality (1-100) */
  quality?: number;
  /** Sizes attribute for responsive images */
  sizes?: string;
  /** Predefined aspect ratio variant (only works with fill=true) */
  aspectRatio?: AspectRatioVariant;
  /** Dataset name */
  dataset?: string;
} & Omit<ImageProps, "src">;

/**
 * Options for building image URLs
 */
export type ImageUrlOptions = {
  width: number;
  height: number;
  fit?: string;
  quality?: number;
};
